import { createContext, ReactNode, useContext, useReducer } from "react";
import {
  INIT_DATA,
  UPDATE_SHOW_REPORT_RESULT_PAGE,
  FETCH_COMMENTS,
  FETCH_REPLY_COMMENTS,
  ADD_COMMENT,
  ADD_REPLY_COMMENT,
  EDIT_COMMENT,
  EDIT_REPLY_COMMENT,
  DELETE_COMMENT,
  DELETE_REPLY_COMMENT,
  UPDATE_PINNED_COMMENT_STATUS,
  UPDATE_LIKED_COMMENT_STATUS,
  REPORT_COMMENT,
  REPORT_BOARD,
  REPORT_REPLY_COMMENT,
  EVENT_SOURCE_API,
  UPDATE_SHOW_PENDING_APPROVAL_COMMENT_PAGE,
  UPDATE_NEW_NICKNAME_AVATAR,
  UPDATE_LIKED_BOARD_STATUS,
  UPDATE_SNACKBAR,
} from "../utils/boardDetailConstants";

import {
  BoardContextType,
  BoardState,
  BoardAction,
  Comment,
  Board,
  Snackbar,
} from "../types/boardDetail";

export const BoardDetailContext = createContext<BoardContextType | null>(null);

const boardDetailReducer = (state: BoardState, action: BoardAction) => {
  switch (action.type) {
    case INIT_DATA: {
      return {
        ...(state ?? {}),
        boardDetailData: action.payload.data || {},
        totalComments: action.payload.total_comments || 0,
      };
    }
    case UPDATE_SHOW_REPORT_RESULT_PAGE: {
      return {
        ...state,
        showReportResultPage: action.payload,
      };
    }
    case FETCH_COMMENTS: {
      return {
        ...(state ?? {}),
        ...action.payload,
        comments: [
          ...(state?.comments ?? []),
          ...action.payload.nextPageComments,
        ],
        nextCursor: action.payload.nextCursor,
        hasPinnedComment: action.payload.hasPinnedComment,
      };
    }
    case FETCH_REPLY_COMMENTS: {
      const newComments = state?.comments?.map((comment) => {
        if (comment.id === action.payload.parentId) {
          return {
            ...comment,
            children: action.payload.allReplyComments,
          };
        }
        return comment;
      });
      return {
        ...(state ?? {}),
        comments: newComments,
      };
    }
    case ADD_COMMENT: {
      const countAddedComments = action.payload.total_reply_comments
        ? action.payload.total_reply_comments + 1
        : 1;
      const hasCommentFlag = Object.hasOwn(action.payload, "comment_flag");
      const newBoardDetail = {
        ...state.boardDetailData,
      };
      if (hasCommentFlag) {
        newBoardDetail.comment_flg = action.payload.comment_flag;
      }
      delete action.payload.comment_flag;
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        comments: [action.payload, ...(state?.comments ?? [])],
        totalComments: state?.totalComments + countAddedComments,
      };
    }
    case ADD_REPLY_COMMENT: {
      const newComments = state?.comments?.map((comment) => {
        if (comment.id === action.payload.parentId) {
          if (
            (Array.isArray(comment?.children) && comment.children.length > 0) ||
            action.payload.eventSource === EVENT_SOURCE_API
          ) {
            return {
              ...comment,
              total_reply_comments:
                Number(comment.total_reply_comments || 0) + 1,
              children: [
                action.payload.newComment,
                ...(comment?.children ?? []),
              ],
            };
          }
          return {
            ...comment,
            total_reply_comments: Number(comment.total_reply_comments || 0) + 1,
          };
        }
        return comment;
      });
      const hasCommentFlag = Object.hasOwn(
        action.payload.newComment,
        "comment_flag"
      );
      const newBoardDetail = {
        ...state.boardDetailData,
      };
      if (hasCommentFlag) {
        newBoardDetail.comment_flg = action.payload.newComment.comment_flag;
      }
      delete action.payload.comment_flag;
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        comments: newComments,
        totalComments: state?.totalComments + 1,
      };
    }
    case EDIT_COMMENT: {
      const updateComments = state?.comments?.map((comment) => {
        if (comment?.id === Number(action.payload.newComment?.id)) {
          return {
            ...comment,
            ...action.payload.newComment,
          };
        }
        return comment;
      });
      const newBoardDetail = {
        ...state.boardDetailData,
        comment_flg: 1,
      };
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        comments: updateComments,
      };
    }
    case EDIT_REPLY_COMMENT: {
      const newComments = state?.comments?.map((comment) => {
        if (comment?.id === Number(action.payload.parentId)) {
          const children = comment?.children?.map((child: Comment) => {
            if (child.id === Number(action.payload.commentId)) {
              return { ...child, ...action.payload.newComment };
            }
            return child;
          });
          return {
            ...comment,
            children: children,
          };
        }
        return comment;
      });
      const newBoardDetail = {
        ...state.boardDetailData,
        comment_flg: 1,
      };
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        comments: newComments,
      };
    }
    case DELETE_COMMENT: {
      const newComments = state.comments?.filter((comment) => {
        return comment.id !== action.payload.commentId;
      });
      const newBoardDetail = {
        ...state.boardDetailData,
      };
      newBoardDetail.comment_flg =
        action.payload.checkCommentFlagOnDeleteComment;

      return {
        ...(state ?? {}),
        comments: newComments,
        boardDetailData: newBoardDetail,
        totalComments: Math.max(
          state.totalComments - action.payload.totalCommentsDeleted,
          0
        ),
      };
    }
    case DELETE_REPLY_COMMENT: {
      const newComments = state.comments?.map((comment) => {
        if (comment.id === action.payload.parentId) {
          if (Array.isArray(comment?.children) && comment.children.length > 0) {
            return {
              ...comment,
              total_reply_comments: Number(comment.total_reply_comments) - 1,
              children: comment.children.filter(
                (child: Comment) => child.id !== action.payload.commentId
              ),
            };
          }
          return {
            ...comment,
            total_reply_comments: Math.max(
              Number(comment.total_reply_comments) - 1,
              0
            ),
          };
        }
        return comment;
      });
      const newBoardDetail = {
        ...state.boardDetailData,
        comment_flg: action.payload.checkCommentFlagOnDeleteReplyComment,
      };
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        comments: newComments,
        totalComments: Math.max(state.totalComments - 1, 0),
      };
    }
    case UPDATE_PINNED_COMMENT_STATUS: {
      const newComments = state.comments?.map((comment) => {
        if (comment?.id === Number(action.payload.commentId)) {
          return { ...comment, is_pinned: Number(action.payload.isPinned) };
        }
        return comment;
      });
      return {
        ...(state ?? {}),
        comments: newComments,
        hasPinnedComment: action.payload.isPinned,
      };
    }
    case UPDATE_LIKED_COMMENT_STATUS: {
      const newComments = state.comments?.map((comment) => {
        if (!action.payload?.parentId) {
          if (comment?.id === Number(action.payload.rootId)) {
            return {
              ...comment,
              like_flg: action.payload.isUpdateByCurrentUser
                ? action.payload.likeFlag
                : comment.like_flg,
              total_of_likes: !!action.payload.likeFlag
                ? Number(comment.total_of_likes) + 1
                : Math.max(Number(comment.total_of_likes || 0) - 1, 0),
            };
          }
          return comment;
        } else {
          if (comment?.id === Number(action.payload?.parentId)) {
            const newChildren = comment?.children?.map((child: any) => {
              if (child?.id === Number(action.payload.rootId)) {
                return {
                  ...child,
                  like_flg: action.payload.isUpdateByCurrentUser
                    ? action.payload.likeFlag
                    : child.like_flg,
                  total_of_likes: !!action.payload.likeFlag
                    ? Number(child.total_of_likes) + 1
                    : Math.max(Number(child.total_of_likes || 0) - 1, 0),
                };
              }
              return child;
            });
            return { ...comment, children: newChildren };
          }
          return comment;
        }
      });
      return { ...state, comments: newComments };
    }
    case REPORT_COMMENT: {
      const newComments = state.comments?.filter((comment) => {
        return comment.id !== action.payload.commentId;
      });
      const newBoardDetail = {
        ...state.boardDetailData,
        comment_flg: action.payload.checkCommentFlagOnReportComment,
      };
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        comments: newComments,
        showReportResultPage:
          action.payload.eventSource === EVENT_SOURCE_API
            ? true
            : state.showReportResultPage,
        totalComments: Math.max(
          state.totalComments - action.payload.countReportedItems,
          0
        ),
      };
    }
    case REPORT_BOARD: {
      return {
        ...(state ?? {}),
        showReportResultPage: true,
        boardDetailData: {},
      };
    }
    case REPORT_REPLY_COMMENT: {
      const newComments = state.comments?.map((comment) => {
        if (comment.id === action.payload.parentId) {
          if (Array.isArray(comment?.children) && comment.children.length > 0) {
            return {
              ...comment,
              total_reply_comments: Number(comment.total_reply_comments) - 1,
              children: comment.children.filter(
                (child: Comment) => child.id !== action.payload.commentId
              ),
            };
          }
          return {
            ...comment,
            total_reply_comments: Math.max(
              Number(comment.total_reply_comments) - 1,
              0
            ),
          };
        }
        return comment;
      });
      const newBoardDetail = {
        ...state.boardDetailData,
        comment_flg: action.payload.checkCommentFlagOnReportReplyComment,
      };
      return {
        ...(state ?? {}),
        boardDetailData: newBoardDetail,
        showReportResultPage:
          action.payload.eventSource === EVENT_SOURCE_API
            ? true
            : state.showReportResultPage,
        comments: newComments,
        totalComments: Math.max(state.totalComments - 1, 0),
      };
    }
    case UPDATE_SHOW_PENDING_APPROVAL_COMMENT_PAGE: {
      return {
        ...state,
        showPendingApprovalCommentPage:
          action.payload.showPendingApprovalCommentPage,
      };
    }

    case UPDATE_NEW_NICKNAME_AVATAR: {
      const { uid, newAvatarUrl, newNickname, name } = action.payload ?? {};
      const newBoardDetail = state.boardDetailData ?? ({} as any);
      const canUpdateDetail = action.payload.uid === state.boardDetailData?.uid;
      const newComments = state.comments?.map((comment: Comment) => {
        const {
          children = [],
          nickname: commentNickname,
          avatar_url: commentAvatarUrl,
          uid: commentUid,
        } = comment ?? {};
        if (!uid) {
          return comment;
        }

        let commentChildren = children ?? [];
        if (Array.isArray(commentChildren) && commentChildren.length !== 0) {
          commentChildren = commentChildren.map((child: Comment) => {
            const {
              uid: childUid,
              nickname: childNickname,
              avatar_url: childAvatarUrl,
            } = child ?? {};
            return {
              ...child,
              nickname:
                childUid === uid
                  ? newNickname
                    ? newNickname
                    : name
                  : childNickname,
              avatar_url: childUid === uid ? newAvatarUrl : childAvatarUrl,
            };
          });
        }
        return {
          ...comment,
          nickname:
            commentUid === uid
              ? newNickname
                ? newNickname
                : name
              : commentNickname,
          avatar_url: commentUid === uid ? newAvatarUrl : commentAvatarUrl,
          children: commentChildren,
        };
      });
      if (canUpdateDetail) {
        newBoardDetail.nickname = newNickname
          ? newNickname
          : `${state.boardDetailData?.sei}${state.boardDetailData?.mei}`;
        newBoardDetail.avatar_url = newAvatarUrl
          ? newAvatarUrl
          : state.boardDetailData?.avatar_url;
      }
      return {
        ...state,
        boardDetailData: newBoardDetail,
        comments: newComments,
      };
    }
    case UPDATE_LIKED_BOARD_STATUS: {
      const likeFlag = action.payload.likeFlag;
      const isUpdateByCurrentUser = action.payload.isUpdateByCurrentUser;

      const newBoardDetail = {
        ...state.boardDetailData,
        like_flg: isUpdateByCurrentUser
          ? likeFlag
          : state.boardDetailData?.like_flg,
        total_of_likes: !!likeFlag
          ? Number(state.boardDetailData?.total_of_likes) + 1
          : Math.max(Number(state.boardDetailData?.total_of_likes || 0) - 1, 0),
      };
      return {
        ...state,
        boardDetailData: newBoardDetail,
      };
    }
    case UPDATE_SNACKBAR: {
      return {
        ...state,
        snackbar: action.payload.snackbar,
      };
    }
    default:
      return state;
  }
};

export const BoardDetailProvider = (props: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(boardDetailReducer, {
    boardDetailData: {},
    showReportResultPage: false,
    comments: [],
    totalComments: 0,
    hasMore: true,
    nextCursor: null,
    hasPinnedComment: false,
    showPendingApprovalCommentPage: false,
    snackbar: { severity: "success", message: "" },
  });
  const { children } = props;

  const initData = (data: Board | null, total_comments: number) => {
    dispatch({ type: INIT_DATA, payload: { data, total_comments } });
  };

  const fetchComments = (
    nextPageComments: Comment[],
    hasMore: boolean,
    nextCursor?: string | null,
    hasPinnedComment?: boolean
  ) => {
    dispatch({
      type: FETCH_COMMENTS,
      payload: { nextPageComments, hasMore, nextCursor, hasPinnedComment },
    });
  };

  const fetchAllReplyComments = (
    parentId: number,
    allReplyComments: Comment[]
  ) => {
    dispatch({
      type: FETCH_REPLY_COMMENTS,
      payload: { parentId, allReplyComments },
    });
  };

  const addComment = (newComment: Comment) => {
    dispatch({ type: ADD_COMMENT, payload: newComment });
  };

  const addReplyComment = (
    parentId: number,
    newComment: any,
    eventSource: string = EVENT_SOURCE_API
  ) => {
    dispatch({
      type: ADD_REPLY_COMMENT,
      payload: { parentId, newComment, eventSource },
    });
  };

  const editComment = (commentId: number, newComment: any) => {
    dispatch({ type: EDIT_COMMENT, payload: { commentId, newComment } });
  };

  const editReplyComment = (
    parentId: number,
    commentId: number,
    newComment: Comment
  ) => {
    dispatch({
      type: EDIT_REPLY_COMMENT,
      payload: { parentId, commentId, newComment },
    });
  };

  const deleteComment = (
    commentId: number,
    totalCommentsDeleted: number,
    checkCommentFlagOnDeleteComment: number
  ) => {
    dispatch({
      type: DELETE_COMMENT,
      payload: {
        commentId,
        totalCommentsDeleted,
        checkCommentFlagOnDeleteComment,
      },
    });
  };

  const deleteReplyComment = (
    parentId: number,
    commentId: number,
    checkCommentFlagOnDeleteReplyComment: number
  ) => {
    dispatch({
      type: DELETE_REPLY_COMMENT,
      payload: { parentId, commentId, checkCommentFlagOnDeleteReplyComment },
    });
  };

  const reportComment = (
    commentId: number,
    countReportedItems: number,
    checkCommentFlagOnReportComment: number,
    eventSource: string = EVENT_SOURCE_API
  ) => {
    dispatch({
      type: REPORT_COMMENT,
      payload: {
        commentId,
        countReportedItems,
        checkCommentFlagOnReportComment,
        eventSource,
      },
    });
  };

  const reportBoard = () => {
    dispatch({
      type: REPORT_BOARD,
      payload: null,
    });
  };

  const reportReplyComment = (
    parentId: number,
    commentId: number,
    checkCommentFlagOnReportReplyComment: number,
    eventSource: string = EVENT_SOURCE_API
  ) => {
    dispatch({
      type: REPORT_REPLY_COMMENT,
      payload: {
        parentId,
        commentId,
        checkCommentFlagOnReportReplyComment,
        eventSource,
      },
    });
  };

  const updateShowReportResultPage = (showReportResultPage: boolean) => {
    dispatch({
      type: UPDATE_SHOW_REPORT_RESULT_PAGE,
      payload: showReportResultPage,
    });
  };

  const updatePinnedCommentStatus = (commentId: number, isPinned: boolean) => {
    dispatch({
      type: UPDATE_PINNED_COMMENT_STATUS,
      payload: { commentId, isPinned },
    });
  };

  const updateLikedCommentStatus = (
    parentId: number,
    rootId: number,
    likeFlag: number,
    isUpdateByCurrentUser: boolean
  ) => {
    dispatch({
      type: UPDATE_LIKED_COMMENT_STATUS,
      payload: { parentId, rootId, likeFlag, isUpdateByCurrentUser },
    });
  };

  const updateShowPendingApprovalCommentPage = (
    showPendingApprovalCommentPage: boolean
  ) => {
    dispatch({
      type: UPDATE_SHOW_PENDING_APPROVAL_COMMENT_PAGE,
      payload: { showPendingApprovalCommentPage },
    });
  };

  const updateNewNicknameAvatar = (
    uid: string,
    newNickname: string,
    newAvatarUrl: string,
    name: string
  ) => {
    dispatch({
      type: UPDATE_NEW_NICKNAME_AVATAR,
      payload: { uid, newNickname, newAvatarUrl, name },
    });
  };

  const updateLikedBoardStatus = (
    likeFlag: number,
    isUpdateByCurrentUser: boolean
  ) => {
    dispatch({
      type: UPDATE_LIKED_BOARD_STATUS,
      payload: { likeFlag, isUpdateByCurrentUser },
    });
  };

  const updateSnackbar = (snackbar: Snackbar) => {
    dispatch({
      type: UPDATE_SNACKBAR,
      payload: { snackbar },
    });
  };

  return (
    <BoardDetailContext.Provider
      value={{
        state,
        initData,
        updateShowReportResultPage,
        fetchComments,
        fetchAllReplyComments,
        addComment,
        addReplyComment,
        editComment,
        editReplyComment,
        deleteComment,
        deleteReplyComment,
        reportComment,
        reportBoard,
        reportReplyComment,
        updatePinnedCommentStatus,
        updateLikedCommentStatus,
        updateShowPendingApprovalCommentPage,
        updateNewNicknameAvatar,
        updateLikedBoardStatus,
        updateSnackbar,
      }}
    >
      {children}
    </BoardDetailContext.Provider>
  );
};

export const useBoard = () => {
  const context = useContext(BoardDetailContext);
  if (!context) {
    throw new Error("");
  }
  return context;
};
